import { AxiosError } from 'axios';
import { Platform } from 'react-native';

interface ApiErrorData {
  message?: string;
  detail?: string;
}

export type ErrorKind = 'network' | 'timeout' | 'server' | 'auth' | 'client' | 'unknown';

/**
 * Classify an error into a category for UI decisions.
 */
export function classifyError(err: unknown): ErrorKind {
  if (err instanceof AxiosError) {
    if (!err.response) {
      return err.code === 'ECONNABORTED' ? 'timeout' : 'network';
    }
    const status = err.response.status;
    if (status === 401 || status === 403) return 'auth';
    if (status >= 500) return 'server';
    return 'client';
  }
  return 'unknown';
}

/**
 * Check whether an error is transient (network issue, timeout, or server error)
 * as opposed to a definitive client/auth error from the server.
 */
export function isTransientError(err: unknown): boolean {
  if (err instanceof AxiosError) {
    return (
      !err.response ||
      err.code === 'ERR_NETWORK' ||
      err.code === 'ECONNABORTED' ||
      (err.response?.status !== undefined && err.response.status >= 500)
    );
  }
  return false;
}

/**
 * "No internet" only when the device says so. On the web a response-less
 * failure also covers CORS rejections, blocked mixed content, TLS errors and a
 * wrong API address — none of which anyone fixes by checking their Wi-Fi, and
 * login.tsx notes that IdP discovery is the usual cause there. Native keeps
 * the plain message: a request that never got a response is nearly always the
 * connection.
 */
function unreachableMessage(): string {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (Platform.OS !== 'web' || offline) {
    return 'No internet connection. Please check your network.';
  }
  return 'Could not reach the server. Your connection looks fine, so the server may be down or the browser may be blocking the request.';
}

/**
 * Return a user-facing message based on the error type.
 */
export function getUserFacingMessage(err: unknown, fallback?: string): string {
  switch (classifyError(err)) {
    case 'network':
      return unreachableMessage();
    case 'timeout':
      return 'Request timed out. The server may be slow or unavailable.';
    case 'server':
      return 'Server is temporarily unavailable. Please try again later.';
    case 'auth':
      return 'Session expired. Please log in again.';
    default:
      return getErrorMessage(err, fallback ?? 'Something went wrong.');
  }
}

/**
 * Extract a user-facing error message from a caught error.
 *
 * Handles:
 *  - AxiosError with `.response.data.message` or `.detail`
 *  - Plain `Error` instances
 *  - Unknown values (returns fallback)
 */
export function getErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as ApiErrorData | undefined;
    return data?.message || data?.detail || fallback;
  }
  if (err instanceof Error) {
    return err.message || fallback;
  }
  return fallback;
}
